import React, { forwardRef } from 'react';
import { ArrowRight, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { buildPageList } from '../../utils/gmpBoard';
import { CM_BAND_RISK, CONSENSUS, PLATFORMS, cmBand } from '../../utils/ipoReviews';

// Rows shown per page; the table has no size picker
const PAGE_SIZE = 10;

const CONSENSUS_STYLES = {
  strong_apply: 'bg-emerald-600 border-emerald-600 text-white shadow-sm',
  must_subscribe: 'bg-emerald-100 border-emerald-200 text-emerald-800 px-3 py-1.5',
  apply: 'bg-emerald-50 border-emerald-200 text-emerald-700',
  may_apply: 'bg-amber-50 border-amber-200 text-amber-700',
  neutral: 'bg-slate-100 border-slate-200 text-slate-700',
  active_seekers: 'bg-violet-50 border-violet-200 text-violet-700',
  avoid: 'bg-rose-50 border-rose-200 text-rose-600',
  under_review: 'bg-slate-50 border-slate-200 text-slate-500',
  awaited: 'bg-slate-50 border-slate-200 text-slate-500',
};

const CALL_TONES = { positive: 'text-emerald-600', cautious: 'text-amber-600', neutral: 'text-slate-500', negative: 'text-rose-600' };

const NOTE_STYLES = {
  quote: 'italic text-slate-500',
  plain: 'text-slate-600',
  negative: 'text-rose-600',
  pending: 'italic text-slate-400',
};

const SCORE_STYLES = {
  must: 'border-emerald-300 bg-emerald-50 text-emerald-700',
  avoid: 'border-rose-200 bg-rose-50 text-rose-600',
  default: 'border-slate-200 bg-slate-50 text-slate-600',
};

const RISK_TONES = { must: 'text-emerald-600', may: 'text-amber-600', high: 'text-violet-600', avoid: 'text-rose-600' };

const PLATFORM_STYLES = {
  Mainboard: 'border-teal-200 bg-teal-50 text-[#006666]',
  default: 'border-amber-200 bg-amber-50 text-amber-700',
};

const FIELD_CLASS =
  'w-full px-2.5 py-1.5 bg-white border border-slate-200 rounded-lg text-[11px] font-medium normal-case tracking-normal text-slate-700 placeholder-slate-400 focus:outline-none focus:border-[#008080] focus:ring-1 focus:ring-[#008080]';

const isFeatured = (review) => review.consensus === 'strong_apply';

const ScoreCell = ({ review }) => {
  if (review.cmScore === null) {
    return (
      <>
        <span className="text-slate-300" aria-hidden="true">
          –
        </span>
        <span className="sr-only">Not rated yet</span>
      </>
    );
  }
  const band = cmBand(review.cmScore);
  const tone = SCORE_STYLES[band] || SCORE_STYLES.default;
  if (isFeatured(review)) {
    return (
      <span className="inline-flex flex-col items-center gap-1">
        <span className={`px-2.5 py-1.5 rounded-lg border text-sm font-extrabold tabular-nums whitespace-nowrap ${tone}`}>
          {review.cmScore} <span className="font-semibold opacity-60">/ 100</span>
        </span>
        <span className={`text-[10px] font-semibold ${RISK_TONES[band]}`}>{CM_BAND_RISK[band]}</span>
      </span>
    );
  }
  return (
    <span className={`inline-flex min-w-[2.25rem] justify-center px-1.5 py-1 rounded-md border text-xs font-bold tabular-nums ${tone}`}>
      {review.cmScore}
    </span>
  );
};

const ResearchCell = ({ review }) => (
  <div className="flex flex-col items-start gap-1.5 max-w-[300px]">
    {review.ratings?.map((rating) => (
      <span
        key={rating.analyst}
        className="inline-flex items-start justify-between gap-3 max-w-full rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-[11px] leading-snug"
      >
        <span className="text-slate-700">{rating.analyst}:</span>
        <span className={`font-semibold text-right ${CALL_TONES[rating.tone]}`}>{rating.call}</span>
      </span>
    ))}
    {review.note && (
      <p className={`text-[11px] sm:text-xs leading-relaxed ${NOTE_STYLES[review.noteStyle]}`}>
        {review.noteStyle === 'quote' ? `“${review.note}”` : review.note}
        {review.recommendation && (
          <>
            {' '}
            Recommendation: <strong className="font-bold text-slate-800">{review.recommendation}</strong>
          </>
        )}
      </p>
    )}
  </div>
);

const ReportCell = ({ review, onOpenReport }) => {
  if (!review.report) return <span className="text-xs text-slate-300">Pending</span>;
  if (review.report === 'pdf') {
    return (
      <button
        type="button"
        onClick={() => onOpenReport(review)}
        aria-label={`PDF Note: ${review.company}`}
        className="inline-flex items-center gap-1 text-xs font-bold text-[#008080] hover:text-[#006666] hover:underline underline-offset-2 whitespace-nowrap"
      >
        PDF Note
        <ArrowRight className="w-3.5 h-3.5" />
      </button>
    );
  }
  return (
    <button
      type="button"
      onClick={() => onOpenReport(review)}
      aria-label={`View Note: ${review.company}`}
      className="text-xs font-medium text-slate-400 hover:text-slate-700 hover:underline underline-offset-2 whitespace-nowrap"
    >
      View Note
    </button>
  );
};

const PageButton = ({ onClick, disabled, label, children }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    aria-label={label}
    className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-slate-200 bg-white text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
  >
    {children}
  </button>
);

export const ReviewsTable = forwardRef(
  ({ reviews, page, onPageChange, filters, onFilterChange, onOpenReport, emptyMessage }, ref) => {
    const totalPages = Math.max(1, Math.ceil(reviews.length / PAGE_SIZE));
    const currentPage = Math.min(page, totalPages);
    const pageRows = reviews.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
    const firstIndex = reviews.length ? (currentPage - 1) * PAGE_SIZE + 1 : 0;
    const lastIndex = Math.min(currentPage * PAGE_SIZE, reviews.length);
    const showing = `Showing ${firstIndex} to ${lastIndex} of ${reviews.length} entries`;

    return (
      <div ref={ref} className="scroll-mt-20 space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2 px-1 text-xs text-slate-500">
          <p className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span>{showing}</span>
            <span className="hidden sm:inline text-slate-300" aria-hidden="true">
              |
            </span>
            <span>
              Sorted by: <strong className="font-semibold text-slate-700">Latest Advisory Updates</strong>
            </span>
          </p>
        </div>

        <section aria-label="IPO reviews" className="bg-white rounded-2xl border border-slate-200/90 shadow-sm overflow-hidden">
          <div className="overflow-x-auto scrollbar-slim">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50/90 border-b border-slate-200/80 text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                  <th scope="col" className="sticky left-0 z-10 bg-slate-50 py-3 pl-4 sm:pl-5 pr-3 min-w-[170px] sm:min-w-[230px]">
                    Company / Sector
                  </th>
                  <th scope="col" className="py-3 px-2 whitespace-nowrap">Type</th>
                  <th scope="col" className="py-3 px-2 text-center whitespace-nowrap">CM Score</th>
                  <th scope="col" className="py-3 px-3 min-w-[220px]">Broker Ratings &amp; Research Verdict</th>
                  <th scope="col" className="py-3 px-2 text-center whitespace-nowrap">Consensus</th>
                  <th scope="col" className="py-3 pl-2 pr-4 sm:pr-5 text-right whitespace-nowrap">Report</th>
                </tr>
                <tr className="border-b border-slate-200/80">
                  <td className="sticky left-0 z-10 bg-white py-2.5 pl-4 sm:pl-5 pr-3">
                    <input
                      type="search"
                      value={filters.company}
                      onChange={(e) => onFilterChange('company', e.target.value)}
                      placeholder="Search company..."
                      aria-label="Search company or sector"
                      className={FIELD_CLASS}
                    />
                  </td>
                  <td className="py-2.5 px-2">
                    <select
                      value={filters.platform}
                      onChange={(e) => onFilterChange('platform', e.target.value)}
                      aria-label="Filter by type"
                      className={`${FIELD_CLASS} w-auto`}
                    >
                      <option value="all">All</option>
                      {PLATFORMS.map((platform) => (
                        <option key={platform} value={platform}>
                          {platform}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="py-2.5 px-2">
                    <input
                      type="number"
                      inputMode="numeric"
                      min={0}
                      max={100}
                      value={filters.minScore}
                      onChange={(e) => onFilterChange('minScore', e.target.value)}
                      placeholder="Min CM"
                      aria-label="Minimum CM score"
                      className={`${FIELD_CLASS} w-16 mx-auto block text-center`}
                    />
                  </td>
                  <td className="py-2.5 px-3">
                    <input
                      type="search"
                      value={filters.analyst}
                      onChange={(e) => onFilterChange('analyst', e.target.value)}
                      placeholder="Search analyst or broker..."
                      aria-label="Search analyst or broker"
                      className={FIELD_CLASS}
                    />
                  </td>
                  <td className="py-2.5 px-2">
                    <select
                      value={filters.consensus}
                      onChange={(e) => onFilterChange('consensus', e.target.value)}
                      aria-label="Filter by consensus"
                      className={FIELD_CLASS}
                    >
                      <option value="all">All Verdicts</option>
                      {Object.entries(CONSENSUS).map(([key, meta]) => (
                        <option key={key} value={key}>
                          {meta.label}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="py-2.5 pl-2 pr-4 sm:pr-5" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-xs sm:text-[13px]">
                {pageRows.length === 0 && (
                  <tr>
                    <td colSpan={6} className="py-12 text-center text-sm text-slate-500">
                      {emptyMessage}
                    </td>
                  </tr>
                )}
                {pageRows.map((review) => {
                  const featured = isFeatured(review);
                  const lowScore = cmBand(review.cmScore) === 'avoid';
                  const rowTint = featured ? 'bg-emerald-50/50' : lowScore ? 'bg-rose-50/40' : 'hover:bg-slate-50/70';
                  const stickyTint = featured ? 'bg-emerald-50' : lowScore ? 'bg-rose-50' : 'bg-white group-hover:bg-slate-50';
                  return (
                    <tr key={review.id} className={`group align-middle transition-colors ${rowTint}`}>
                      <td className={`sticky left-0 z-10 py-3.5 pl-4 sm:pl-5 pr-3 ${stickyTint}`}>
                        <p className="font-bold text-slate-900">{review.company}</p>
                        <p className="mt-0.5 flex items-start gap-1.5 text-[11px] text-slate-400">
                          {featured && <span className="mt-1 w-1.5 h-1.5 rounded-full bg-[#008080] shrink-0" aria-hidden="true" />}
                          {review.sector}
                        </p>
                      </td>
                      <td className="py-3.5 px-2">
                        <span
                          className={`inline-flex px-2 py-0.5 rounded-full border text-[10px] font-extrabold uppercase tracking-wide whitespace-nowrap ${
                            PLATFORM_STYLES[review.platform] || PLATFORM_STYLES.default
                          }`}
                        >
                          {review.platform}
                        </span>
                      </td>
                      {/* relative keeps ScoreCell's sr-only text inside the scrolling table */}
                      <td className="relative py-3.5 px-2 text-center">
                        <ScoreCell review={review} />
                      </td>
                      <td className="py-3.5 px-3">
                        <ResearchCell review={review} />
                      </td>
                      <td className="py-3.5 px-2 text-center">
                        <span
                          className={`inline-flex justify-center text-center px-2.5 py-1 rounded-md border text-[11px] sm:text-xs font-bold max-w-[120px] ${
                            CONSENSUS_STYLES[review.consensus]
                          }`}
                        >
                          {CONSENSUS[review.consensus].label}
                        </span>
                      </td>
                      <td className="py-3.5 pl-2 pr-4 sm:pr-5 text-right">
                        <ReportCell review={review} onOpenReport={onOpenReport} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="flex flex-col md:flex-row items-center justify-between gap-3 px-4 sm:px-5 py-3.5 border-t border-slate-200/80 bg-slate-50/70">
            <p className="text-xs text-slate-500">{showing}</p>
            {totalPages > 1 && (
              <nav className="flex flex-wrap items-center justify-center gap-1" aria-label="Reviews table pagination">
                <PageButton onClick={() => onPageChange(1)} disabled={currentPage <= 1} label="First page">
                  <ChevronsLeft className="w-3.5 h-3.5" />
                  First
                </PageButton>
                <PageButton onClick={() => onPageChange(currentPage - 1)} disabled={currentPage <= 1} label="Previous page">
                  <ChevronLeft className="w-3.5 h-3.5" />
                  Prev
                </PageButton>
                {buildPageList(currentPage, totalPages).map((p, i) =>
                  p === '…' ? (
                    <span key={`gap-${i}`} className="w-7 text-center text-xs text-slate-400">
                      …
                    </span>
                  ) : (
                    <button
                      key={p}
                      type="button"
                      onClick={() => onPageChange(p)}
                      aria-current={p === currentPage ? 'page' : undefined}
                      className={`w-8 h-8 rounded-lg text-xs font-bold border ${
                        p === currentPage ? 'bg-[#008080] border-[#008080] text-white' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                      }`}
                    >
                      {p}
                    </button>
                  )
                )}
                <PageButton onClick={() => onPageChange(currentPage + 1)} disabled={currentPage >= totalPages} label="Next page">
                  Next
                  <ChevronRight className="w-3.5 h-3.5" />
                </PageButton>
                <PageButton onClick={() => onPageChange(totalPages)} disabled={currentPage >= totalPages} label="Last page">
                  Last
                  <ChevronsRight className="w-3.5 h-3.5" />
                </PageButton>
              </nav>
            )}
          </div>
        </section>
      </div>
    );
  }
);

ReviewsTable.displayName = 'ReviewsTable';

export default ReviewsTable;
